import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { compareVersions, type BuildSummary } from '@va/shared';
import { desktop, type InstalledItem, type InstallProgress } from '../platform';

interface InstallsState {
  /** True inside the desktop app (installing is a desktop-only feature). */
  available: boolean;
  items: InstalledItem[];
  progress: Record<string, InstallProgress>;
  isInstalled(productId: string): boolean;
  get(productId: string): InstalledItem | undefined;
  install(product: { id: string; name: string }, build: BuildSummary): Promise<void>;
  uninstall(productId: string): Promise<void>;
  /** Opens the install's folder; 'app' = the app half of a plugin + app product (Downloads). */
  reveal(productId: string, part?: 'main' | 'app'): Promise<void>;
  refresh(): Promise<void>;
}

const Ctx = createContext<InstallsState | null>(null);

/** What to tell the user after an install or update, by where the build went. */
export function installedMessage(name: string, installType: string, opts: { demo?: boolean; updatedTo?: string } = {}): string {
  const what = opts.demo ? `${name} demo` : name;
  const done = opts.updatedTo ? `updated to v${opts.updatedTo}` : 'installed';
  switch (installType) {
    case 'ffgl-app':
      return `${what} ${done}: the plugin is in Extra Effects and the app is in your Downloads folder (Show app opens it). Restart Resolume to load the plugin.`;
    case 'app-zip':
      return `${what} ${done} in your Downloads folder.`;
    case 'installer':
      return `${what}: the installer is in your Downloads folder and has been opened.`;
    case 'files':
      return `${what} ${done}.`;
    default:
      return `${what} ${done}. Restart Resolume to ${opts.demo ? 'try it (with the VA watermark)' : 'load it'}.`;
  }
}

export function InstallsProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<InstalledItem[]>([]);
  const [progress, setProgress] = useState<Record<string, InstallProgress>>({});

  const refresh = useCallback(async () => {
    if (desktop) setItems(await desktop.installs.list());
  }, []);

  useEffect(() => {
    if (!desktop) return;
    void refresh();
    return desktop.installs.onProgress((p) => {
      setProgress((cur) => {
        const next = { ...cur, [p.productId]: p };
        if (p.phase === 'done') delete next[p.productId];
        return next;
      });
      if (p.phase === 'done' || p.phase === 'error') void refresh();
    });
  }, [refresh]);

  const value = useMemo<InstallsState>(
    () => ({
      available: !!desktop,
      items,
      progress,
      isInstalled: (id) => items.some((i) => i.productId === id),
      get: (id) => items.find((i) => i.productId === id),
      install: async (product, build) => {
        if (!desktop) return;
        setProgress((cur) => ({ ...cur, [product.id]: { productId: product.id, phase: 'download', received: 0, total: build.sizeBytes } }));
        try {
          await desktop.installs.install({ productId: product.id, productName: product.name, build });
        } finally {
          setProgress((cur) => {
            const next = { ...cur };
            delete next[product.id];
            return next;
          });
          await refresh();
        }
      },
      uninstall: async (id) => {
        if (!desktop) return;
        await desktop.installs.uninstall(id);
        await refresh();
      },
      reveal: async (id, part) => {
        if (desktop) await desktop.installs.reveal(id, part);
      },
      refresh,
    }),
    [items, progress, refresh],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useInstalls(): InstallsState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useInstalls outside InstallsProvider');
  return ctx;
}

/** Best build for this computer: native platform first, then platform-independent. */
export function pickBuild(builds: BuildSummary[], includeBeta = false): BuildSummary | undefined {
  const os = desktop?.platform ?? (navigator.userAgent.includes('Mac') ? 'darwin' : 'win32');
  const order = os === 'darwin' ? ['mac-universal', 'mac-arm64', 'mac-x64', 'any'] : ['win-x64', 'win-arm64', 'any'];
  const candidates = builds.filter((b) => order.includes(b.platform) && (includeBeta || b.channel === 'stable'));
  candidates.sort((a, b) => compareVersions(b.version, a.version) || order.indexOf(a.platform) - order.indexOf(b.platform));
  return candidates[0];
}
